import React from "react";
import {
  FeatherAlertCircle,
  FeatherAlertTriangle,
  FeatherDownloadCloud,
  FeatherFileText,
} from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { GstBacklog, isFieldLocked } from "../../domain/entities/CompanySettings";
import { GST_SECTIONS } from "../config/gstForm";
import { GstChecksSection } from "../components/GstChecksSection";
import { GstLocationsLine } from "../components/GstLocationsLine";
import { GstZohoBlock } from "../components/GstZohoBlock";
import { SettingsPanel } from "../components/SettingsPanel";
import { SettingsSection } from "../components/SettingsSection";
import { SettingsLayout } from "../layout/SettingsLayout";
import { useGstPanel } from "../hooks/useGstPanel";

const PanelSkeleton: React.FC = () => (
  <div className="flex w-full grow shrink-0 basis-0 items-center justify-center py-16">
    <div className="flex flex-col items-center gap-4">
      <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-primary" />
      <span className="text-body font-body text-subtext-color">Loading GST settings...</span>
    </div>
  </div>
);

const PanelError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="flex w-full grow shrink-0 basis-0 items-center justify-center py-16">
    <div className="flex max-w-md flex-col items-center gap-4 text-center">
      <FeatherAlertCircle className="text-heading-1 font-heading-1 text-error-600" />
      <span className="text-heading-3 font-heading-3 text-default-font">
        We couldn't load your GST settings
      </span>
      <span className="text-body font-body text-subtext-color">{message}</span>
      <Button onClick={onRetry}>Try again</Button>
    </div>
  </div>
);

/**
 * ZOUR-19 — what happens to documents already in the queue when the date changes.
 *
 * Reported, never acted on. ZOUR-19 deliberately does not re-validate the
 * backlog: those documents keep the review flags they already have. But the
 * date IS applied when each one is pushed, so a reviewer who looked at a
 * pre-date bill yesterday will see it reach Zoho without tax. Saying that
 * plainly is the whole point of this note — a silent change to work somebody
 * has already reviewed is the failure mode worth avoiding.
 */
const BacklogNote: React.FC<{ backlog: GstBacklog | null; hasDate: boolean }> = ({
  backlog,
  hasDate,
}) => {
  if (!backlog || backlog.total === null || backlog.total === 0) {
    return null;
  }

  return (
    <div className="flex w-full items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
      <FeatherFileText className="mt-0.5 shrink-0 text-body font-body text-subtext-color" />
      <div className="flex flex-col gap-1">
        <span className="text-body-bold font-body-bold text-default-font">
          {backlog.total} document{backlog.total === 1 ? "" : "s"} waiting to be reviewed or
          pushed
        </span>
        <span className="text-body font-body text-subtext-color">
          {backlog.invoices ?? 0} invoice{(backlog.invoices ?? 0) === 1 ? "" : "s"} and{" "}
          {backlog.bills ?? 0} bill{(backlog.bills ?? 0) === 1 ? "" : "s"}. These keep their
          existing review flags. Nothing is re-checked
          {hasDate ? " when you change the date" : ""}. The date is still applied when each one
          is pushed to your books.
        </span>
      </div>
    </div>
  );
};

/**
 * GstPage — `/settings/gst`.
 *
 * Five blocks, in the order a reader needs them:
 *
 *   1. Your GST registration     your own GSTIN — hidden entirely until GST is
 *                                SAVED on, or a GSTIN is already stored
 *                                (items 25 + 27). A locked empty field is a
 *                                question a non-GST workspace cannot answer,
 *                                and by waiting for the save the section
 *                                arrives with the GSTIN already filled from
 *                                Zoho
 *   2. In Zoho Books             what your books say, read live, with the
 *                                manual step spelled out; the FIRST block on
 *                                the page for a non-GST tenant
 *   3. In Zournal                when GST applies, plus the backlog note and
 *                                the tax sync
 *   4. Checks                    what to look at after switching GST on — next
 *                                to the date whose consequences it reports
 *   5. Party GSTIN verification  checking OTHER parties' GSTINs, a different
 *                                subject from everything above it
 *
 * Blocks 1, 3 and 5 come from GST_SECTIONS; 2 and 4 are rendered here because
 * neither is a form. The two Zoho-facing blocks sit next to the answers they
 * inform rather than being collected at the top.
 *
 * Backed by `api/v1/settings/gst`, NOT the older
 * `api/settings/gstin-verification` that the /admin screen uses. The difference
 * matters: the old endpoint returns the Cashfree client secret in its response
 * body, and the settings area has no role gate, so every signed-in user would
 * receive a payment-gateway credential. The v1 endpoint reports only whether a
 * secret is stored, which is what `SecretFieldRow` renders.
 *
 * ZOUR-19 added the GST toggle and the boundary date. The toggle was left off
 * this panel on 2026-08-08 because it is baked into the login payload and a
 * control here would have appeared to do nothing until the next sign-in;
 * `useGstPanel` re-stores the company in the browser on every save, which is
 * the fix rather than the same omission twice.
 */
export const GstPage: React.FC = () => {
  const {
    settings,
    panelSettings,
    values,
    loading,
    saving,
    error,
    fieldErrors,
    dirty,
    saved,
    setField,
    save,
    discard,
    reload,
    zoho,
    backlog,
    refreshingZoho,
    refreshZoho,
    syncingTaxes,
    taxSyncNote,
    syncTaxes,
    checks,
    runningChecks,
    checksError,
    runChecks,
    adoptingGstin,
    adoptGstinError,
    adoptZohoGstin,
  } = useGstPanel();

  if (loading) {
    return (
      <SettingsLayout>
        <PanelSkeleton />
      </SettingsLayout>
    );
  }

  if (!settings || !panelSettings) {
    return (
      <SettingsLayout>
        <PanelError message={error ?? "Something went wrong."} onRetry={reload} />
      </SettingsLayout>
    );
  }

  const warnings = settings.warnings ?? [];
  const hasDate = (values.gst_enabled_from ?? "") !== "";

  // The server locks the GST answer for anyone who is not an admin (it also
  // refuses the write). The tax sync is the same privilege — it truncates and
  // refills the workspace's tax list — so the button follows the same signal
  // rather than inventing a second notion of who may do what.
  const gstEditable = !isFieldLocked(settings, "company_gst_enabled");

  // ZOUR-19 item 13: offer the GSTIN from Zoho only when there is genuinely
  // something to do — our field is blank, Zoho reports one, and this user may
  // write it. A migrating client is in exactly this state and has no other way
  // to fill a field the panel keeps locked.
  // ZOUR-19 items 25 + 27: the registration section is only meaningful once GST
  // is in play. A workspace that is not registered has no GSTIN and no way to
  // get one, so a locked empty field at the top of the page is a question with
  // no answer.
  //
  // Keyed on the SAVED value, not the live form value (item 27 revises item 25).
  // Flipping the toggle now changes nothing above In Zournal until Save. That is
  // the better behaviour for two reasons: a section appearing and disappearing
  // as a switch is toggled makes the page jump under the reader, and until the
  // save happens the section would show an EMPTY read-only GSTIN — the server
  // fills it from Zoho as part of the off -> on save (item 13), so waiting means
  // the section arrives already answered, with the "copied from Zoho Books" note
  // beside it.
  //
  // A stored GSTIN always shows, whatever the toggle says: hiding a value
  // somebody has is worse than showing one they do not need.
  const savedValues = settings.values;
  const showRegistration =
    savedValues.company_gst_enabled === "1" ||
    (savedValues.gst_number ?? "").trim() !== "";

  // Same saved-value basis as showRegistration: the button lives inside that
  // section, and the GSTIN it fills is a server-side write, not a form field.
  const canAdoptZohoGstin =
    gstEditable &&
    (savedValues.gst_number ?? "").trim() === "" &&
    Boolean(zoho?.reachable && zoho?.gstin);

  return (
    <SettingsLayout>
      <SettingsPanel
        title="GST"
        description="Your GST registration, what Zoho Books says, and when GST applies."
        error={error}
        saved={saved}
        savedMessage="GST settings saved."
        dirty={dirty}
        saving={saving}
        onSave={save}
        onDiscard={discard}
      >
        {/* Saved, but the user still needs to hear about it — a date that
            disagrees with Zoho's, or Zoho unreachable when we wanted to check.
            Amber, not red: the save committed. */}
        {warnings.length > 0 && !dirty && (
          <div className="flex w-full flex-col gap-2 rounded-md border border-solid border-warning-200 bg-warning-50 px-4 py-3">
            {warnings.map((warning) => (
              <div key={warning} className="flex items-start gap-2">
                <FeatherAlertTriangle className="mt-0.5 shrink-0 text-body font-body text-warning-700" />
                <span className="text-body font-body text-warning-700">{warning}</span>
              </div>
            ))}
          </div>
        )}

        {GST_SECTIONS.map((section) => (
          <React.Fragment key={section.id}>
            {/* Every section renders except "registration" while GST is off and
                no GSTIN is stored — see showRegistration. The Zoho block below
                is deliberately OUTSIDE this condition, so it becomes the first
                thing a non-GST tenant sees: what their books say is exactly
                what they need at that point. */}
            {(section.id !== "registration" || showRegistration) && (
              <SettingsSection
                config={section}
                settings={panelSettings}
                values={values}
                fieldErrors={fieldErrors}
                onChange={setField}
              />
            )}

            {/* Immediately under the toggle and the date, because it is about
                what those two answers do to work already in the queue. */}
            {/* Right after the GSTIN, because it is what fills it. */}
            {section.id === "registration" && showRegistration && canAdoptZohoGstin && (
              <div className="flex w-full flex-col gap-2 border-b border-solid border-neutral-border pb-8">
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="neutral-secondary"
                    icon={<FeatherDownloadCloud />}
                    loading={adoptingGstin}
                    disabled={adoptingGstin}
                    onClick={adoptZohoGstin}
                  >
                    Use Zoho's GSTIN
                  </Button>
                  <span className="text-caption font-caption text-subtext-color">
                    Your GSTIN is blank and your books hold {zoho?.gstin}. Copying it in means your
                    own GSTIN appears on documents as the buyer. Without it, bills are read as B2C
                    and input tax credit is questioned.
                  </span>
                </div>

                {adoptGstinError && (
                  <span className="text-caption font-caption text-error-700">{adoptGstinError}</span>
                )}
              </div>
            )}

            {/* ZOUR-65: how many registrations there are, under the one GSTIN
                this page shows. Above the Zoho block because it qualifies the
                field directly above it — "this is your GSTIN" is only the whole
                story when there is one. */}
            {section.id === "registration" && <GstLocationsLine />}

            {/* Block 2: what the books say, below the GSTIN it should match. */}
            {section.id === "registration" && (
              <GstZohoBlock zoho={zoho} refreshing={refreshingZoho} onRefresh={refreshZoho} />
            )}

            {section.id === "zournal" && (
              <>
                <BacklogNote backlog={backlog} hasDate={hasDate} />

                {gstEditable && (
                <div className="flex w-full flex-col gap-2 border-b border-solid border-neutral-border pb-8">
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      icon={<FeatherDownloadCloud />}
                      loading={syncingTaxes}
                      disabled={syncingTaxes}
                      onClick={syncTaxes}
                    >
                      Sync taxes from Zoho
                    </Button>
                    <span className="text-caption font-caption text-subtext-color">
                      Copies the GST rates from Zoho Books. Runs automatically when you first switch
                      GST on.
                    </span>
                  </div>

                  {taxSyncNote && (
                    <span className="text-caption font-caption text-subtext-color">
                      {taxSyncNote}
                    </span>
                  )}
                </div>
                )}

                {/* ZOUR-19 item 28: Checks sits directly under In Zournal,
                    above Party GSTIN verification. It is about the consequences
                    of the date just set, so it belongs next to it — and party
                    verification is a different subject entirely (other people's
                    GSTINs, not yours). */}
                <GstChecksSection
                  checks={checks}
                  running={runningChecks}
                  error={checksError}
                  onRun={runChecks}
                />
              </>
            )}
          </React.Fragment>
        ))}
      </SettingsPanel>
    </SettingsLayout>
  );
};
